/* eslint-disable @angular-eslint/prefer-standalone */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  Output,
  ViewContainerRef,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DSM3ModalOptionsType, DSM3ModalService } from '@eredes/dsm3';
import {
  ContractsByPodAndSupplierPartnerNumber,
  ContractsByPodAndSupplierPartnerNumberFilters,
  ICustomerInformation,
  ICustomerSearchInformation,
  MsMasterdataService,
  PodAndSupplierPartnerNumberContractResponse,
} from '@eredes/microservices';
import { debounceTime } from 'rxjs/operators';

import { UserModel } from 'app/_core/models/user.model';
import { UserService } from 'app/_core/services/user.service';
import { EdpValidators } from 'app/_shared/edp-validators/edp-validators';
import { NodeTabRequest } from 'app/request/interfaces/request.interface';
import { RequestFormHelper } from 'app/request-form/_helpers/request-form.helper';
import { IAttachment } from 'app/request-form/_models/attachment.interface';
import { GeneralDataViewModel } from 'app/request-form/_models/general-data.view-model';
import { FormConfiguration, FormConfigurationFieldsSubmit } from 'app/request-form/_models/request-form.interface';
import { RequestFormService } from 'app/request-form/_services/request-form.service';
import { DialogAlertComponent } from 'app/_shared/modals/dialog-alert/dialog-alert.component';
import { DSM34Dialog, Dsm34UploadErrorList } from '@eredes/dsm34';

@Component({
  selector: 'app-request-form',
  templateUrl: './request-form.component.html',
  styleUrls: ['./request-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RequestFormComponent implements OnInit {
  @Input() formTabData!: NodeTabRequest;
  @Output() clickBack = new EventEmitter<any>();

  maxFileAccepted = input<number>(10);
  maxSizeAccepted = input<number>(3670016);
  fileTypesAccepted = input<string[]>(['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.xlsx', '.csv', '.sgl', '.sgl_v2', '.zip']);
  formConfiguration!: FormConfiguration;
  form!: UntypedFormGroup;
  user!: UserModel;
  contracts!: PodAndSupplierPartnerNumberContractResponse[];
  customerInformation!: ICustomerInformation;
  selectedPartnerNumber = '';
  selectedContract!: PodAndSupplierPartnerNumberContractResponse;
  anotherNif!: string;
  transactionId!: string;
  generalDataViewModel!: GeneralDataViewModel;
  bytesConverted!: string;

  /**
   * Returns the 'attachments' form control.
   */
  get attachmentsForm(): UntypedFormArray {
    return this.form.get('attachments') as UntypedFormArray;
  }

  /**
   * Returns a placeholder string for describing a request, with an optional asterisk if description is required.
   */
  get descriptionPlaceHolder(): string {
    return `Descreva aqui o pedido${this.formConfiguration.descriptionRequired ? ' *' : ''}`;
  }

  /**
   * Returns a boolean value based on the presence of a description field in the form configuration.
   */
  get showDescription(): boolean {
    return !!this.formConfiguration.submit.fields.description;
  }

  /**
   Returns a boolean value based on the existence of attachments in the form configuration.
   */
  get showAttachments(): boolean {
    return !!this.formConfiguration.submit.fields.attachments;
  }

  private readonly fb = inject(UntypedFormBuilder);
  private readonly msMasterdataService = inject(MsMasterdataService);
  private readonly userService = inject(UserService);
  private readonly modalService = inject(DSM3ModalService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly requestFormService = inject(RequestFormService);
  private readonly router = inject(Router);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _dsm34Dialog = inject(DSM34Dialog);

  ngOnInit(): void {
    this.bytesConverted = this.bytesToSize(this.maxSizeAccepted());
    this.formConfiguration = this.formTabData?.formConfiguration;
    this.user = this.userService.getUser()!;
    this.checkFormPermission();
    this.createForm();
    this.setupEventListeners();
    this.configureGeneralData();
  }

  /**
   * Method to validate the contract selected
   */
  onChooseContract() {
    if (this.form.controls.partnerNumber.value !== 'outro') {
      this.onChooseContractFromList();

      return;
    }

    this.onChooseAnotherContract();
  }

  /**
   * Make the necessary settings when the contract list modal is closed
   */
  onCloseModalContract() {
    const { partnerNumber, anotherNif } = this.form.controls;

    partnerNumber.setValue(this.selectedPartnerNumber);

    if (this.contracts.length === 1) {
      this.form.controls.partnerNumber.setValue('outro');
    }

    if (this.isAnotherSelected() && !anotherNif.valid) {
      anotherNif.setValue('PT');
    }

    if (!this.selectedContract) {
      this.generalDataViewModel.messageErrorPartnerNumber = 'O campo é obrigatório';
    } else {
      this.generalDataViewModel.messageErrorPartnerNumber = '';
    }
  }

  /**
   * Method to submit form data
   */
  submit(): void {
    if (!this.transactionId) {
      this.transactionId = crypto.randomUUID();
    }

    const promiseFilesToUpload = [];
    if (this.attachmentsForm) {
      this.attachmentsForm.value.forEach((fileToBeUploaded: File) => {
        promiseFilesToUpload.push(RequestFormHelper.setReader(fileToBeUploaded));
      });
    }

    const fields: FormConfigurationFieldsSubmit = this.mapperToSubmit();
    Promise.all(promiseFilesToUpload).then((filesToUpload: IAttachment[]) => {
      this.requestFormService.submit(fields, this.formConfiguration.submit, filesToUpload).subscribe({
        next: (response) => {
          if (response.insistence?.isInsistence) {
            const message = `Para a questão colocada já existe um pedido aberto nesta mesma tipologia. Para fazer acompanhamento e pedir mais informação, por favor <a href="/request/solicitation/detail/${response.insistence.uuid}">aceda ao detalhe do pedido ID ${response.insistence.contactId}</a>`;
            const modal = this.modalService.open(this.viewContainerRef, {
              title: 'Ups! Ocorreu um problema...',
              message: message,
              type: DSM3ModalOptionsType.ERROR,
            });
            modal.onClose?.()?.subscribe(() => {
              void this.router.navigate(['request/solicitation']);
            });
          } else {
            this.transactionId = '';
            const message = response.contact_id
              ? `O seu pedido nº ${response.contact_id} foi submetido com sucesso.`
              : 'O seu pedido foi submetido com sucesso.';
            const modal = this.modalService.open(this.viewContainerRef, {
              title: 'Sucesso',
              message: message,
              type: DSM3ModalOptionsType.SUCCESS,
            });
            modal.onClose?.()?.subscribe(() => {
              void this.router.navigate(['request/solicitation']);
            });
          }
        },
        error: () => {
          this.modalService.open(this.viewContainerRef, {
            title: 'Erro',
            message: 'Não foi possível criar o seu pedido',
            type: DSM3ModalOptionsType.ERROR,
          });
        },
      });
    });
  }

  /**
   * Method to return to the previous page
   */
  back() {
    this.clickBack.emit();
  }

  /**
   * Method to clear form data
   */
  clear(): void {
    this.generalDataViewModel.selectedName = '';
    this.generalDataViewModel.selectedNif = '';
    this.generalDataViewModel.messageErrorPartnerNumber = '';
    this.form.controls.cpe.setValue(null);
    this.form.controls.description.setValue(null);
    this.form.controls.attachments.setValue([]);
    this.changeDetectorRef.detectChanges();
  }

  /**
   * The function checks if a form is hidden and navigates to a different route if it is.
   */
  private checkFormPermission(): void {
    if (this.formTabData?.hide) {
      this.router.navigate(['request/solicitation']);
    }
  }

  /**
   * Configure model for general data component
   */
  private configureGeneralData(): void {
    const errorsMessageCPE = {
      required: 'O campo é obrigatório',
      invalidCpe: 'Este não é um CPE válido, tente novamente',
      invalidPn: 'Não existe nenhum Cliente com este CPE associado',
      invalidCpeProduction: 'CPE não corresponde a produção',
      invalidCpeDifferentNetwork: 'O CPE inserido pertence a um Operador de Rede diferente da E-REDES',
    };

    const errorsMessageProcessIdentification = {
      required: 'O campo é obrigatório',
      invalidProcessIdentifier: 'Este não é um ID de processo válido, tente novamente',
    };

    const errorMessageAnotherNif = {
      required: 'O campo é obrigatório',
      invalidTaxNumber: 'NIF inválido, por favor valide este campo',
      invalidTaxNumberPrefix: 'Deve iniciar com o prefixo do país válido',
    };

    const placeHolderCPE = this.formConfiguration.cpeRequired ? 'CPE *' : 'CPE';
    const placeHolderProcessIdentification = this.formConfiguration.processIdentificationRequired
      ? 'Identificador do processo *'
      : 'Identificador do processo';

    const errorsMessageBillingDate = {
      required: 'O campo é obrigatório',
      max: 'O valor deve ser entre 01 e 31',
      min: 'O valor deve ser entre 01 e 31',
      minlength: 'É obrigatório inserir dois dígidos, entre 01 e 31',
    };

    this.generalDataViewModel = new GeneralDataViewModel(
      this.form,
      'cpe',
      errorsMessageCPE,
      placeHolderCPE,
      'processIdentification',
      errorsMessageProcessIdentification,
      placeHolderProcessIdentification,
      this.formConfiguration.showProcessIdentification,
      this.formConfiguration.showBillingdDate,
      'billingDate',
      'Dia*',
      errorsMessageBillingDate,
      'partnerNumber',
      'anotherNif',
      errorMessageAnotherNif,
      false,
      ''
    );
  }

  /**
   * Method responsible for creating the form
   */
  private createForm(): void {
    this.form = this.fb.group({
      cpe: this.configureCpeValidator(),
      partnerNumber: this.configurePartnerNumberValidator(),
      processIdentification: this.configureProcessIdentificationValidator(),
      billingDate: this.configureBillingDateValidator(),
      description: this.configureDescriptionValidator(),
      attachments: new UntypedFormControl([]),
      anotherNif: ['', [], []],
    });
  }

  /**
   * Method to initialize listeners for executing some flow in the component
   */
  private setupEventListeners(): void {
    this.form.controls.cpe.valueChanges.subscribe(() => {
      this.form.controls.billingDate.markAsTouched();
      this.form.controls.billingDate.markAsDirty();
      this.validateCPE();
    });

    this.form.controls.partnerNumber.valueChanges.subscribe(() => {
      this.changePartnerNumber();
    });

    this.form.controls.anotherNif.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.validateAnotherNif();
    });
  }

  /**
   * Method to validate the partner number field and apply rules according to the selected value
   */
  private changePartnerNumber() {
    const { anotherNif } = this.form.controls;
    if (this.isAnotherSelected()) {
      anotherNif.setValidators([Validators.required, EdpValidators.taxNumberWithCountryCode]);
      this.generalDataViewModel.showAnotherNif = true;

      if (!anotherNif.value) {
        anotherNif.setValue('PT');
      }
    } else {
      anotherNif.setValidators([]);
      anotherNif.setErrors(null);
      this.generalDataViewModel.showAnotherNif = false;
    }
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Method to validate the field other NIF calling the customer service
   */
  private validateAnotherNif() {
    const { anotherNif } = this.form.controls;

    if (!anotherNif.value) {
      return;
    }

    if (anotherNif.valid) {
      this.anotherNif = anotherNif.value;
      this.msMasterdataService.customerSearch(anotherNif.value).subscribe({
        next: (response: ICustomerSearchInformation) => {
          this.handleCustomerSearch(response);
        },
        error: () => {
          this.handleCustomerSearch({} as ICustomerSearchInformation);
        },
      });
    }
  }

  /**
   * Method responsible for handling the customer  service response
   * @param response {@link ICustomerSearchInformation}
   */
  private handleCustomerSearch(response: ICustomerSearchInformation) {
    this.customerInformation = null;

    if (response) {
      const { customers } = response;

      if (customers && customers.length > 0) {
        this.customerInformation = customers[0];
      }
    }
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Method to configure fields according to the other informed NIF
   */
  private onChooseAnotherContract() {
    if (!this.customerInformation) {
      this.generalDataViewModel.selectedName = '-';
      this.generalDataViewModel.selectedNif = this.anotherNif;
    } else {
      const { firstName, lastName, taxNumber } = this.customerInformation;
      this.generalDataViewModel.selectedName = `${firstName} ${lastName ? lastName : ''}`;
      this.generalDataViewModel.selectedNif = taxNumber;
    }
    this.selectedPartnerNumber = 'outro';
    this.selectedContract = this.contracts.find((item) => item.partner_number === this.selectedPartnerNumber);
  }

  /**
   * Method to configure fields according to the contract selected in the list of contracts
   */
  private onChooseContractFromList() {
    this.customerInformation = null;
    this.form.controls.anotherNif.setValue('');

    this.selectedPartnerNumber = this.form.controls.partnerNumber.value;

    this.selectedContract = this.contracts.find((item) => item.partner_number === this.selectedPartnerNumber);

    const { nif, contractContacts } = this.selectedContract;
    const { firstName, lastName } = contractContacts[0];

    this.generalDataViewModel.selectedName = `${firstName} ${lastName ? lastName : ''}`;
    this.generalDataViewModel.selectedNif = nif;
  }

  /**
   * Method responsible for validating the value informed in the CPE field
   * and calling the endpoint that lists the NIFs linked to the CPE
   */
  private validateCPE(): void {
    const cpe = this.form.controls.cpe;

    if (!this.isValidDifferentNetworkOperatorCpe()) {
      return;
    }

    if (cpe.valid && this.formConfiguration.clientPartnerNumberRequired) {
      const args: ContractsByPodAndSupplierPartnerNumberFilters = {
        supplierCode: this.user.retailerSupplierId,
        cpe: cpe.value.trim(),
      };

      this.msMasterdataService.getContractsByCpeAndSupplierPartnerNumber(args).subscribe({
        next: (response: ContractsByPodAndSupplierPartnerNumber) => {
          this.handleGetContractsSuccess(response);
        },
        error: () => {
          this.handleGetContractsSuccess({} as ContractsByPodAndSupplierPartnerNumber);
        },
      });
    }
  }

  /**
   * The function checks if the value of a form control is a valid different network operator CPE
   * (Customer Premises Equipment).
   * @returns a boolean value.
   */
  private isValidDifferentNetworkOperatorCpe(): boolean {
    const cpe = this.form.controls.cpe;
    if (!cpe.value || cpe.value === '') {
      return true;
    }
    if (cpe.valid) {
      const invalidCpeDifferentNetwork = EdpValidators.differentNetworkOperatorCpe(cpe);

      if (invalidCpeDifferentNetwork) {
        cpe.setErrors(invalidCpeDifferentNetwork);

        return false;
      }

      cpe.setErrors(null);

      return true;
    }

    return false;
  }

  /**
   * Method responsible for handling the returns (in case of success)
   * by the endpoint that lists the NIFs
   * @param response ContractsByPodAndSupplierResult
   */
  private handleGetContractsSuccess(response: ContractsByPodAndSupplierPartnerNumber): void {
    const { cpe } = this.form.controls;
    let contracts: PodAndSupplierPartnerNumberContractResponse[] = this.removeRepeatContracts(response.contracts);

    if (this.formConfiguration.cpeProductionRequired) {
      contracts = contracts.filter((item) => item.division === 'PR');
    }

    let modalTitle = `Foram encontrados NIFs associados ao CPE ${cpe.value}`;
    if (contracts.length === 0) {
      this.form.controls.partnerNumber.setValue('outro');
      modalTitle = `Não foram encontrados clientes para o CPE ${cpe.value}`;
    }

    this.form.controls.partnerNumber.setValidators([Validators.required]);
    this.addAnotherOptionInContracts(contracts);
    this.contracts = contracts;
    this.generalDataViewModel.modalTitle = modalTitle;
    this.generalDataViewModel.contractsObs.next(contracts);
    this.generalDataViewModel.openModal.next(true);
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Remove repeated contracts from {@link contracts}
   * @param contracts
   * @returns list of contracts {@link PodAndSupplierPartnerNumberContractResponse[]}
   */
  private removeRepeatContracts(
    contracts: PodAndSupplierPartnerNumberContractResponse[]
  ): PodAndSupplierPartnerNumberContractResponse[] {
    if (!contracts) {
      return [];
    }

    return contracts.filter((value, index, self) => index === self.findIndex((t) => t.nif === value.nif));
  }

  /**
   * Adds OTHER option in the list of contracts
   * @param contracts
   */
  private addAnotherOptionInContracts(contracts: PodAndSupplierPartnerNumberContractResponse[]) {
    contracts.push({
      nif: 'Outro',
      partner_number: 'outro',
      cpe: '',
      supplier_partner_number: '',
      contractContacts: [],
      division: '',
      contractData: {
        moveInDate: '',
        moveOutDate: '',
      },
    });
  }

  /**
   * Method to set form CPE field configuration (value, validators)
   * @returns List with form CPE field settings
   */
  private configureCpeValidator(): any[] {
    const validators: any[] = [EdpValidators.cpe];

    if (this.formConfiguration.cpeRequired) {
      validators.push(Validators.required);
    }

    if (this.formConfiguration.cpeProductionRequired) {
      validators.push(EdpValidators.productionCpe);
    }

    return ['', validators, []];
  }

  /**
   * Method to set the form's processIdentification field configuration (value, validators)
   * @returns List with the form's processIdentification field settings
   */
  private configureProcessIdentificationValidator(): any[] {
    const validators = [];

    if (this.formConfiguration.processIdentificationRequired) {
      validators.push(Validators.required);
    }

    if (this.formConfiguration.showProcessIdentification) {
      validators.push(EdpValidators.messagingProcessIdentifier);
    }

    return ['', validators, []];
  }

  /**
   * Method to set the form's partnerNumber field configuration (value, validators)
   * @returns List with the form's partnerNumber field settings
   */
  private configurePartnerNumberValidator(): any[] {
    if (this.formConfiguration.clientPartnerNumberRequired) {
      return ['', [Validators.required], []];
    }

    return ['', [], []];
  }

  /**
   * This function configures a validator for the billing date field based on whether it should be
   * shown or not.
   * @returns An array containing three elements. The first element is null, the second element is an
   * array of validators (required, min, max, and minLength) if the form configuration shows the
   * billing date, and an empty array if it doesn't. The third element is an empty array in both cases.
   */
  private configureBillingDateValidator(): any[] {
    if (this.formConfiguration.showBillingdDate) {
      return [null, [Validators.required, Validators.min(1), Validators.max(31), Validators.minLength(2)], []];
    }

    return [null, [], []];
  }

  /**
   * The function configures a description validator based on the form configuration.
   * @returns An array containing an empty string, an array with a single element Validators.required,
   * and an empty array.
   */
  private configureDescriptionValidator(): any[] {
    if (this.formConfiguration.descriptionRequired) {
      return ['', [Validators.required], []];
    }

    return ['', [], []];
  }

  /**
   * Set up object with the request data informed by the user
   * @returns {@link FormConfigurationFieldsSubmit}
   */
  private mapperToSubmit(): FormConfigurationFieldsSubmit {
    return {
      cpe: this.form.controls.cpe.value,
      processId: this.form.controls.processIdentification.value,
      partnerNumber: this.user.retailerPartnerNumber,
      clientPartnerNumber: this.getClientPartnerNumber(),
      billingDate: this.form.controls.billingDate.value,
      description: this.form.controls.description.value,
      attachments: this.form.controls.attachments.value,
      contractCode: this.user.retailerAccountId,
      transactionId: this.transactionId,
    };
  }

  /**
   * Get the number of the currently selected partner
   * @returns string representing the selected partner number
   */
  private getClientPartnerNumber(): string {
    const { partnerNumber } = this.form.controls;

    if (this.isAnotherSelected()) {
      if (!this.customerInformation) {
        return this.user.retailerPartnerNumber;
      }

      return this.customerInformation.partnerNumber;
    }

    return partnerNumber.value;
  }

  /**
   * Checks if the selected contract option is ANOTHER
   * @returns true if the selected contract option is ANOTHER
   */
  private isAnotherSelected() {
    const { partnerNumber } = this.form.controls;

    return partnerNumber.value === 'outro';
  }

  private showErrors(errors: Dsm34UploadErrorList): void {
    const _foundErrorSize = errors?.errorList.find((error) => error.error === 'size');

    if (_foundErrorSize) {
      this._dsm34Dialog.open(DialogAlertComponent, {
        data: {
          properties: {
            icon: 'alerta',
            title: 'Ups! Ocorreu um problema...',
            message: `Só é permitido anexar ficheiros até ${this.bytesConverted}.`,
          },
        },
      });

      return;
    }

    const _foundErrorType = errors?.errorList.find((error) => error.error === 'type');

    if (_foundErrorType) {
      this._dsm34Dialog.open(DialogAlertComponent, {
        data: {
          properties: {
            icon: 'alerta',
            title: 'Ups! Ocorreu um problema...',
            message: 'O formato que selecionou não é permitido. Verifique os formatos permitidos.',
          },
        },
      });
    }
  }

  /**
   * Returns a byte size number to its size correspondent
   * @param bytes - number
   * @returns string
   */
  private bytesToSize(bytes: number): string {
    const _SIZES = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    if (bytes === 0) return 'n/a';

    const _I = Math.floor(Math.log(bytes) / Math.log(1024));

    if (_I === 0) return bytes + ' ' + _SIZES[_I];

    const convertedVal = bytes / Math.pow(1024, _I);

    if (convertedVal % 1 !== 0) {
      return convertedVal.toFixed(1).replace('.', ',') + ' ' + _SIZES[_I];
    }

    return (bytes / Math.pow(1024, _I)).toFixed(0) + ' ' + _SIZES[_I];
  }
}
